import React from "react";
import { createRoot } from "react-dom/client";
import { SWRConfig } from "swr";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect
} from "react-router-dom";
import "@ww-digital/prism/dist/style.css";
import routes from "./routes";
import Assessment from "./components/Assessment";
import RedirectFirstQuestion from "./components/RedirectFirstQuestion";
import Results from "./components/Results";
import AppXSProvider from "./components/AppXSProvider";
import InitializationPrerequisites from "./components/InitializationPrerequisites";
import AppState from "./components/AppState";
import "./index.scss";
import "@ww-digital/rebrand-web-framework/src/client/webfonts/webfonts.scss";
import useAppContext from "./components/AppContext";
import ResultsRestored from "./components/Results/ResultsRestored";
import ResultsRestoredSequence from "./components/Results/ResultsRestoredSequence";
import ClinicCheckout from "./components/ClinicCheckout";
import { OverlayProvider } from "@ww-digital/web-palette-react/dist/components/Overlay/OverlayProvider/OverlayProvider";
import UrlValidator from "./components/UrlValidator";
import useUrlSlugContext from "./components/UrlSlugContext";
import { loadResultsCookieByBaseUrl } from "./components/Results/storeResultsData";
import ResultsClinical from "components/Results/ResultsClinical";
import type { ResultsCookie } from "types/cookie";
import ResultsRestoredClinical from "components/Results/ResultsRestoredClinical";
import ResultsFoodNoise from "components/Results/ResultsFoodNoise";
import ResultsRestoredFoodNoise from "components/Results/ResultsRestored/ResultsRestoredFoodNoise";
import { ResultsUni5Router } from "components/Results/UnifiedAssessment/ResultsUni5/ResultsUni5Router";
import { ResultsUni5RestoredRouter } from "components/Results/UnifiedAssessment/ResultsUni5/ResultsUni5RestoredRouter";

//IE11 and Android Browser support
if (!Object.entries) {
  Object.entries = function (obj: Record<string, unknown>) {
    const ownProps = Object.keys(obj);
    const length = ownProps.length;
    const resArray = new Array(length); // preallocate the Array
    let i = length;
    while (i--) resArray[i] = [ownProps[i], obj[ownProps[i]]];

    return resArray;
  };
}

const swrConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  errorRetryCount: 2
};

const AppContent = () => {
  const { newQuiz }: any = useAppContext();
  const { baseUrl }: any = useUrlSlugContext();
  const cookie = loadResultsCookieByBaseUrl(baseUrl) as ResultsCookie | null;

  if (newQuiz.isSequenceResultsScreen) {
    return (
      <Router basename={baseUrl}>
        <Switch>
          <Route path={routes.resultsRestoredSequence}>
            <ResultsRestoredSequence />
          </Route>
          <Route path={routes.clinicCheckout}>
            <ClinicCheckout />
          </Route>
          <Route>
            <Redirect to={routes.resultsRestoredSequence} />
          </Route>
        </Switch>
      </Router>
    );
  }

  if (cookie && newQuiz.isClinical) {
    return (
      <Router basename={baseUrl}>
        <Switch>
          <Route path={routes.resultsRestored}>
            <ResultsRestoredClinical />
          </Route>
          <Route path={routes.clinicCheckout}>
            <ClinicCheckout />
          </Route>
          <Route>
            <Redirect to={routes.resultsRestored} />
          </Route>
        </Switch>
      </Router>
    );
  }

  if (cookie && newQuiz.isFoodNoise) {
    return (
      <Router basename={baseUrl}>
        <Switch>
          <Route path={routes.resultsRestored}>
            <ResultsRestoredFoodNoise />
          </Route>
          <Route path={routes.resultsRestored2}>
            <ResultsRestoredFoodNoise page={2} />
          </Route>
          <Route>
            <Redirect to={routes.resultsRestored} />
          </Route>
        </Switch>
      </Router>
    );
  }

  if (cookie && newQuiz.isUni5) {
    return (
      <Router basename={baseUrl}>
        <Switch>
          <Route path={routes.resultsRestored}>
            <ResultsUni5RestoredRouter />
          </Route>
          <Route>
            <Redirect to={routes.resultsRestored} />
          </Route>
        </Switch>
      </Router>
    );
  }

  if (cookie) {
    return (
      <Router basename={baseUrl}>
        <Switch>
          <Route path={routes.resultsRestored}>
            <ResultsRestored />
          </Route>
          {(newQuiz.isUni3 || newQuiz.isUni4) && (
            <Route path={routes.clinicCheckout}>
              <ClinicCheckout />
            </Route>
          )}
          <Route>
            <Redirect to={routes.resultsRestored} />
          </Route>
        </Switch>
      </Router>
    );
  }

  return (
    <Router basename={baseUrl}>
      <AppState>
        <Switch>
          {newQuiz.isClinical && (
            <Route path={routes.results}>
              <ResultsClinical />
            </Route>
          )}
          {newQuiz.isUni5 && (
            <Route path={routes.results}>
             <ResultsUni5Router />
            </Route>
          )}
          {!newQuiz.isClinical && !newQuiz.isUni5 && (
            <Route path={routes.results}>
              <Results />
            </Route>
          )}
          {newQuiz.isFoodNoise && (
            <Route path={routes.results2}>
              <ResultsFoodNoise page={2} />
            </Route>
          )}
          {(newQuiz.isSequence ||
            newQuiz.isUni3 ||
            newQuiz.isUni4 ||
            newQuiz.isClinical) && (
            <Route path={routes.clinicCheckout}>
              <ClinicCheckout />
            </Route>
          )}
          <Route path={routes.question(":questionPath")}>
            <Assessment />
          </Route>
          <Route>
            <RedirectFirstQuestion />
          </Route>
        </Switch>
      </AppState>
    </Router>
  );
};

const root = createRoot(document.getElementById("root")!);

root.render(
  <React.StrictMode>
    <SWRConfig value={swrConfig}>
      <AppXSProvider>
        <OverlayProvider>
          <UrlValidator>
            <InitializationPrerequisites>
              <div id="appTop" tabIndex={-1} />
              <AppContent />
            </InitializationPrerequisites>
          </UrlValidator>
        </OverlayProvider>
      </AppXSProvider>
    </SWRConfig>
  </React.StrictMode>
);
